import FormikCombobox from "@components/form/FormikCombobox";
import FormikDatepicker from "@components/form/FormikDatepicker";
import FormikInput from "@components/form/FormikInput";
import FormikRadioGroup from "@components/form/FormikRadioGroup";
import SectionHeading from "@components/SectionHeading";
import { PlantOption } from "@service/PlantService";
import { FacilityOption } from "@service/FacilityService";
import { countries } from "@utils/countries";
import { states } from "@utils/states";
import { Field } from "formik";
import { useTranslation } from "react-i18next";

interface UpdateContractorFormProps {
  plants: PlantOption[];
  facilities: FacilityOption[];
  onPlantChange: (plantId?: number) => void;
  touched: Record<string, any>;
  errors: Record<string, any>;
  values: Record<string, any>;
  isLoading: boolean;
  success: boolean;
}

export default function UpdateContractorForm({
  plants,
  facilities,
  onPlantChange,
  values,
  errors,
  touched,
}: UpdateContractorFormProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* Personal Information */}
      <SectionHeading className="tw-mt-6">
        {t("UpdateContractorFields.personalInfo")}
      </SectionHeading>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        <Field
          name="firstName"
          label={t("UpdateContractorFields.firstNameLabel")}
          as={FormikInput}
          error={touched.firstName && errors.firstName}
        />
        <Field
          name="secondFirstName"
          label={t("UpdateContractorFields.secondFirstNameLabel")}
          as={FormikInput}
          error={touched.secondFirstName && errors.secondFirstName}
        />
        <Field
          name="lastName"
          label={t("UpdateContractorFields.lastNameLabel")}
          as={FormikInput}
          error={touched.lastName && errors.lastName}
        />
        <Field
          name="secondLastName"
          label={t("UpdateContractorFields.secondLastNameLabel")}
          as={FormikInput}
          error={touched.secondLastName && errors.secondLastName}
        />
        <Field
          name="streetAddress"
          label={t("UpdateContractorFields.streetAddressLabel")}
          as={FormikInput}
          error={touched.streetAddress && errors.streetAddress}
        />
        <Field
          name="city"
          label={t("UpdateContractorFields.city")}
          as={FormikInput}
          error={touched.city && errors.city}
        />
        <Field
          name="zipCode"
          label={t("UpdateContractorFields.zipCode")}
          as={FormikInput}
          error={touched.zipCode && errors.zipCode}
        />
        <Field
          name="state"
          label={t("UpdateContractorFields.state")}
          options={states}
          pageSize={0}
          as={FormikCombobox}
          error={touched.state && errors.state}
        />
        <Field
          name="country"
          label={t("UpdateContractorFields.country")}
          options={countries}
          pageSize={0}
          as={FormikCombobox}
          error={touched.country && errors.country}
        />
        <Field
          name="birthDate"
          label={t("UpdateContractorFields.birthdateLabel")}
          as={FormikDatepicker}
          error={touched.birthDate && errors.birthDate}
        />
        <Field
          name="preferredLang"
          label={t("UpdateContractorFields.preferredLangLabel")}
          options={[
            { value: t("UpdateContractorFields.english") },
            { value: t("UpdateContractorFields.spanish") },
            { value: t("UpdateContractorFields.french") },
            { value: t("UpdateContractorFields.hatianCreole") },
          ]}
          as={FormikCombobox}
          error={touched.preferredLang && errors.preferredLang}
        />
      </div>

      {/* Contact Information */}
      <SectionHeading className="tw-mt-6">
        {t("UpdateContractorFields.contactInfo")}
      </SectionHeading>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        <Field
          name="email"
          label={t("UpdateContractorFields.email")}
          as={FormikInput}
          type="email"
          error={touched.email && errors.email}
        />
        <Field
          name="phone"
          label={t("UpdateContractorFields.phone")}
          as={FormikInput}
          inputMode="numeric"
          error={touched.phone && errors.phone}
        />
      </div>

      {/* Employment Information */}
      <SectionHeading className="tw-mt-6">
        {t("UpdateContractorFields.employmentInfo")}
      </SectionHeading>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        <Field
          name="plant"
          label={t("UpdateContractorFields.plant")}
          options={plants}
          as={FormikCombobox}
          onOptionChange={(opt: PlantOption) => onPlantChange(opt?.id)}
          error={touched.plant && errors.plant}
        />
        <Field
          name="facility"
          label={t("UpdateContractorFields.facility")}
          options={facilities}
          disabled={!values.plant}
          as={FormikCombobox}
          error={touched.facility && errors.facility}
        />
        <Field
          name="contractorId"
          label={t("UpdateContractorFields.contractorId")}
          as={FormikInput}
          inputMode="numeric"
          error={touched.contractorId && errors.contractorId}
        />
        <Field
          name="firstWorkingDay"
          label={t("UpdateContractorFields.firstWorkDayLabel")}
          as={FormikDatepicker}
          error={touched.firstWorkingDay && errors.firstWorkingDay}
        />
        <Field
          name="driversLicense"
          label={t("UpdateContractorFields.driversLicenseLabel")}
          options={[
            { value: "yes", label: t("UpdateContractorFields.yes") },
            { value: "no", label: t("UpdateContractorFields.no") },
          ]}
          as={FormikRadioGroup}
          error={touched.driversLicense && errors.driversLicense}
        />
      </div>

      {/* Payment Information */}
      <SectionHeading className="tw-mt-6">
        {t("UpdateContractorFields.paymentInfo")}
      </SectionHeading>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        <Field
          name="payType"
          label={t("UpdateContractorFields.payTypeLabel")}
          options={[
            { value: t("UpdateContractorFields.check") },
            {
              value: t("UpdateContractorFields.directDeposit"),
            },
          ]}
          as={FormikCombobox}
          error={touched.payType && errors.payType}
        />
        {/* {values.payType === t("UpdateContractorFields.directDeposit") && (
          <Field
            name="bankAccountNumber"
            label={t("UpdateContractorFields.bankAccountNumberLabel")}
            as={FormikInput}
            error={touched.bankAccountNumber && errors.bankAccountNumber}
          />
        )} */}
        <Field
          name="employeeType"
          label={t("UpdateContractorFields.employeeTypeLabel")}
          options={[
            { value: "W2", label: "W2" },
            { value: "1099", label: "1099" },
          ]}
          as={FormikRadioGroup}
          error={touched.employeeType && errors.employeeType}
        />
        {/* <Field
          name="payRateClass"
          label={t("UpdateContractorFields.payRateClassLabel")}
          options={[
            { value: "hourly", label: t("UpdateContractorFields.hourly") },
            { value: "daily", label: t("UpdateContractorFields.daily") },
            { value: "weekly", label: t("UpdateContractorFields.weekly") },
          ]}
          as={FormikRadioGroup}
          error={touched.payRateClass && errors.payRateClass}
        />
        <Field
          name="payRateStartDate"
          label={t("UpdateContractorFields.payRateStartDateLabel")}
          as={FormikDatepicker}
          error={touched.payRateStartDate && errors.payRateStartDate}
        /> */}
        {/* <Field
          name="payRate"
          label={t("UpdateContractorFields.payRateLabel")}
          as={FormikInput}
          inputMode="numeric"
          error={touched.payRate && errors.payRate}
        /> */}

        {/* <Field
          name="billingRateClass"
          label={t("UpdateContractorFields.billingRateClassLabel")}
          options={[
            { value: "hourly", label: t("UpdateContractorFields.hourly") },
            { value: "daily", label: t("UpdateContractorFields.daily") },
            { value: "weekly", label: t("UpdateContractorFields.weekly") },
          ]}
          as={FormikRadioGroup}
          error={touched.billingRateClass && errors.billingRateClass}
        />
        <Field
          name="billingRateStartDate"
          label={t("UpdateContractorFields.billingRateStartDateLabel")}
          as={FormikDatepicker}
          error={touched.billingRateStartDate && errors.billingRateStartDate}
        />
        <Field
          name="billingRate"
          label={t("UpdateContractorFields.billingRateLabel")}
          as={FormikInput}
          inputMode="numeric"
          error={touched.billingRate && errors.billingRate}
        /> */}
        <Field
          name="taxWithHolding"
          label={t("UpdateContractorFields.taxWithholdingLabel")}
          as={FormikInput}
          inputMode="numeric"
          error={touched.taxWithHolding && errors.taxWithHolding}
        />
      </div>

      {/* Deductions */}
      <SectionHeading className="tw-mt-6">
        {t("UpdateContractorFields.deductions")}
      </SectionHeading>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        <Field
          name="rentDeduction"
          label={t("UpdateContractorFields.rent")}
          as={FormikInput}
          inputMode="numeric"
          error={touched.rentDeduction && errors.rentDeduction}
        />
        <Field
          name="adminDeduction"
          label={t("UpdateContractorFields.admin")}
          as={FormikInput}
          inputMode="numeric"
          error={touched.adminDeduction && errors.adminDeduction}
        />
        <Field
          name="visaBondDeduction"
          label={t("UpdateContractorFields.visaBondLabel")}
          as={FormikInput}
          inputMode="numeric"
          error={touched.visaBondDeduction && errors.visaBondDeduction}
        />
        <Field
          name="miscDeduction"
          label={t("UpdateContractorFields.misc")}
          as={FormikInput}
          inputMode="numeric"
          error={touched.miscDeduction && errors.miscDeduction}
        />
      </div>

      {/*
        Status editing has moved to the dedicated Status modal in ContractorList.
        Keep engagement fields out of contractor edit to avoid split update paths.
      */}
    </>
  );
}
